import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { http } from "@/lib/api";
import { Header, Footer } from "@/components/fc/parts";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  const sessionId = params.get("session_id");
  const dossier = params.get("dossier");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const id = data.dossier_id || dossier;
          navigate(`/synthesis/${id}`, { replace: true });
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempts.current >= 25) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, dossier, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-lg rounded-2xl border border-border bg-card p-8 text-center" data-testid="payment-return">
          {status === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto mb-4 animate-spin" style={{ color: "var(--fc-navy)" }} />
              <h1 className="font-serif text-2xl font-semibold text-foreground">Confirmation du paiement…</h1>
              <p className="mt-3 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
                Merci. Nous confirmons votre paiement et préparons votre fiche de synthèse. Cette étape ne
                prend que quelques secondes — merci de ne pas fermer cette page.
              </p>
            </>
          )}
          {(status === "timeout" || status === "error") && (
            <>
              <AlertCircle size={40} className="mx-auto mb-4" style={{ color: "var(--fc-danger)" }} />
              <h1 className="font-serif text-2xl font-semibold text-foreground">Confirmation en cours</h1>
              <p className="mt-3 text-sm" style={{ color: "var(--fc-secondary-text)" }}>
                La confirmation prend un peu plus de temps que prévu. Dès que le paiement est validé, votre
                fiche vous est envoyée par e-mail. Vous pouvez aussi réessayer d'ouvrir votre fiche.
              </p>
              {dossier && (
                <button
                  type="button"
                  onClick={() => navigate(`/synthesis/${dossier}`)}
                  data-testid="open-synthesis-button"
                  className="fc-navy-btn mt-6 w-full rounded-xl px-6 py-3 text-sm font-semibold"
                >
                  Ouvrir ma fiche
                </button>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
